<template>
	<div class="recording-interface" @click.stop>
		<!-- Подсказки жестов -->
		<gesture-hints
			:show-hints="showGestureHints"
			:is-locked="isLocked"
			:is-dragging-up="isDraggingUp"
			:is-dragging-right="isDraggingRight"
		/>
		<!-- Основной контейнер -->
		<div class="recording-container">
			<!-- Кнопка микрофона -->
			<q-btn
				ref="micButton"
				:icon="micIcon"
				round
				:color="micColor"
				class="microphone-btn full-height"
				@click="handleMicClick"
				@mousedown="startDrag"
				@touchstart="startDrag"
				@contextmenu.prevent
				:class="{
					'recording-active': isRecording && !isLocked,
					'locked-mode': isLocked,
					'cancel-mode': isCancelling,
					'ready-mode': isMicrophoneReady && !isRecording
				}"
			/>

			<!-- Панель прогресса при обычной записи -->
			<recording-panel
				:is-recording="isRecording"
				:is-locked="isLocked"
				:progress="progress"
				:formatted-duration="formattedDuration"
				@stop="stopRecording"
				@cancel="cancelRecording"
			/>
		</div>
	</div>
</template>

<script>
import { defineComponent } from 'vue';
import RecordingPanel from '@/components/MessageTypes/FileUploader/VoiceRecorder/RecordingPanel.vue';
import GestureHints from '@/components/MessageTypes/FileUploader/VoiceRecorder/GestureHints.vue';

export default defineComponent({
	name: 'index',
	components: { GestureHints, RecordingPanel },

	data() {
		return {
			isRecording: false,
			isLocked: false,
			isCancelling: false,
			isMicrophoneReady: false,
			mediaRecorder: null,
			audioChunks: [],
			stream: null,
			recordingTime: 0,
			timerInterval: null,
			maxDuration: 120,
			recordingStartTime: 0,

			// Жесты
			dragStart: null,
			currentDrag: null,
			showGestureHints: false,
			isDraggingUp: false,
			isDraggingRight: false,
			dragThreshold: 40,
			dragHoldTimeout: null,
			isHolding: false,

			getUserMedia: null
		};
	},
	computed: {
		formattedDuration() {
			const m = Math.floor(this.recordingTime / 60)
				.toString()
				.padStart(2, '0');
			const s = (this.recordingTime % 60).toString().padStart(2, '0');
			return `${m}:${s}`;
		},
		progress() {
			return this.recordingTime / this.maxDuration;
		},
		micIcon() {
			if (this.isRecording) return 'mic';
			if (!this.isMicrophoneReady) return 'mic_off';
			return 'mic';
		},
		micColor() {
			if (!this.isMicrophoneReady) return 'grey';
			if (this.isRecording && !this.isLocked) return 'red';
			if (this.isLocked) return 'green';
			return 'primary';
		}
	},
	methods: {
		async initializeMicrophone() {
			try {
				// Получаем доступ к микрофону без начала записи
				await this.getMicrophoneAccess();
				this.isMicrophoneReady = true;
			} catch (error) {
				this.isMicrophoneReady = false;
			}
		},

		async getMicrophoneAccess() {
			return new Promise((resolve, reject) => {
				// Проверяем поддержку mediaDevices
				if (!navigator.mediaDevices) {
					// Пробуем префиксные версии для старых браузеров
					this.getUserMedia =
						navigator.getUserMedia ||
						navigator.webkitGetUserMedia ||
						navigator.mozGetUserMedia ||
						navigator.msGetUserMedia;

					if (!this.getUserMedia) {
						reject(new Error('getUserMedia is not supported in this browser'));
						return;
					}

					// Используем префиксную версию
					this.getUserMedia.call(
						navigator,
						{ audio: true },
						(stream) => {
							this.stream = stream;
							resolve(stream);
						},
						reject
					);
				} else {
					// Используем стандартный API
					navigator.mediaDevices
						.getUserMedia({ audio: true })
						.then((stream) => {
							this.stream = stream;
							resolve(stream);
						})
						.catch(reject);
				}
			});
		},
		// Начало перетаскивания
		async startDrag(event) {
			if (this.isLocked || !this.isMicrophoneReady) {
				if (!this.isMicrophoneReady) {
					this.requestMicrophoneAccess();
				}
				return;
			}

			const clientX = event.clientX || (event.touches && event.touches[0].clientX);
			const clientY = event.clientY || (event.touches && event.touches[0].clientY);

			this.dragStart = { x: clientX, y: clientY };
			this.currentDrag = { x: clientX, y: clientY };
			this.isCancelling = false;
			this.isDraggingUp = false;
			this.isDraggingRight = false;
			this.isHolding = false;

			// Добавляем обработчики движения
			if (event.type === 'mousedown') {
				document.addEventListener('mousemove', this.handleDrag);
				document.addEventListener('mouseup', this.endDrag);
			} else {
				document.addEventListener('touchmove', this.handleDrag, { passive: false });
				document.addEventListener('touchend', this.endDrag);
			}

			this.dragHoldTimeout = setTimeout(async () => {
				this.isHolding = true;
				await this.startRecording();
				this.showGestureHints = true;
			}, 250);
		},

		requestMicrophoneAccess() {
			this.initializeMicrophone()
				.then(() => {
					this.$q.notify({
						type: 'positive',
						message: this.$t('microphoneIsReady'),
						timeout: 2000
					});
				})
				.catch((error) => {
					this.handleRecordingError(error);
				});
		},

		// Обработка перетаскивания
		handleDrag(event) {
			if (!this.dragStart) return;

			event.preventDefault();

			const clientX = event.clientX || (event.touches && event.touches[0].clientX);
			const clientY = event.clientY || (event.touches && event.touches[0].clientY);

			this.currentDrag = { x: clientX, y: clientY };

			const deltaX = clientX - this.dragStart.x;
			const deltaY = this.dragStart.y - clientY; // инвертируем Y, так как вверх - отрицательное значение

			// Сбрасываем состояния
			this.isDraggingUp = false;
			this.isDraggingRight = false;

			// Определяем направление жеста
			if (Math.abs(deltaY) > Math.abs(deltaX)) {
				// Вертикальное движение - блокировка
				if (deltaY > this.dragThreshold) {
					this.isDraggingUp = true;
				}
			} else {
				// Горизонтальное движение - отмена
				if (Math.abs(deltaX) > this.dragThreshold) {
					this.isDraggingRight = true;
				}
			}
		},

		// Завершение перетаскивания
		endDrag() {
			clearTimeout(this.dragHoldTimeout);
			this.removeDragListeners();

			// Если не удерживал кнопку — просто выходим
			if (!this.isHolding) {
				this.dragStart = null;
				this.currentDrag = null;
				return;
			}

			if (this.isDraggingUp) {
				this.isLocked = true;
			} else if (this.isDraggingRight) {
				this.cancelRecording();
			} else {
				this.stopRecording();
			}
			// Скрываем подсказки
			this.showGestureHints = false;
			this.isDraggingUp = false;
			this.isDraggingRight = false;

			this.dragStart = null;
			this.currentDrag = null;
		},

		// Удаление обработчиков перетаскивания
		removeDragListeners() {
			document.removeEventListener('mousemove', this.handleDrag);
			document.removeEventListener('mouseup', this.endDrag);
			document.removeEventListener('touchmove', this.handleDrag);
			document.removeEventListener('touchend', this.endDrag);
		},

		// Обработка клика по кнопке (для locked режима)
		handleMicClick() {
			if (this.isLocked) {
				this.stopRecording();
			} else if (!this.isMicrophoneReady) {
				this.requestMicrophoneAccess();
			}
		},

		async startRecording() {
			if (this.isRecording || !this.isMicrophoneReady) return;

			try {
				if (!navigator.mediaDevices.getUserMedia) {
					// Пробуем префиксные версии для старых браузеров
					const getUserMedia =
						navigator.getUserMedia ||
						navigator.webkitGetUserMedia ||
						navigator.mozGetUserMedia ||
						navigator.msGetUserMedia;

					// Если есть префиксная версия, создаем промис для нее
					this.stream = await new Promise((resolve, reject) => {
						getUserMedia.call(navigator, { audio: true }, resolve, reject);
					});
				} else {
					this.stream = await navigator.mediaDevices.getUserMedia({
						audio: true
					});
				}

				// Определяем поддерживаемый формат
				const mimeType = this.getSupportedMimeType();

				let mediaRecorder;
				try {
					mediaRecorder = new MediaRecorder(this.stream, {
						mimeType: mimeType,
						audioBitsPerSecond: 128000
					});
				} catch (recorderError) {
					mediaRecorder = new MediaRecorder(this.stream, {
						audioBitsPerSecond: 128000
					});
				}

				this.mediaRecorder = mediaRecorder;
				this.audioChunks = [];
				this.recordingTime = 0;
				this.recordingStartTime = Date.now();

				this.mediaRecorder.ondataavailable = (e) => {
					if (e.data.size > 0) this.audioChunks.push(e.data);
				};

				this.mediaRecorder.onstop = () => {
					if (this.isCancelling) {
						// Запись отменена, очищаем данные
						this.audioChunks = [];
					} else {
						// Сохраняем запись
						this.saveRecording();
					}

					this.clearRecording();
				};

				this.mediaRecorder.onerror = (event) => {
					this.handleRecordingError(event.error);
					this.clearRecording();
				};

				this.mediaRecorder.start(100);
				this.isRecording = true;

				this.timerInterval = setInterval(() => {
					this.recordingTime = Math.floor((Date.now() - this.recordingStartTime) / 1000);

					if (this.recordingTime >= this.maxDuration) {
						this.stopRecording();
					}
				}, 100);
			} catch (err) {
				this.handleRecordingError(err);
			}
		},

		getSupportedMimeType() {
			const supportedTypes = [
				'audio/webm;codecs=opus',
				'audio/webm',
				'audio/ogg;codecs=opus',
				'audio/aac',
				'audio/mp4'
			];

			for (const type of supportedTypes) {
				if (MediaRecorder.isTypeSupported(type)) {
					return type;
				}
			}
			return 'audio/webm';
		},

		saveRecording() {
			try {
				const mimeType = this.mediaRecorder?.mimeType || 'audio/webm';
				const blob = new Blob(this.audioChunks, { type: mimeType });

				// Определяем расширение файла
				let extension = 'webm';
				if (mimeType.includes('mp4')) extension = 'mp4';
				else if (mimeType.includes('ogg')) extension = 'ogg';
				else if (mimeType.includes('aac')) extension = 'aac';

				const fileName = `recording-${Date.now()}.${extension}`;
				const file = new File([blob], fileName, { type: mimeType });

				// Сохранение записи локально для дебагинга
				// const url = URL.createObjectURL(blob);
				// const a = document.createElement('a');
				// a.href = url;
				// a.download = fileName;
				// document.body.appendChild(a);
				// a.click();
				// document.body.removeChild(a);
				// URL.revokeObjectURL(url);

				this.$emit('uploadRecording', file, this.formattedDuration);
			} catch (error) {
				this.$q.notify({
					type: 'negative',
					message: this.$t('saveRecordingError')
				});
			}
		},

		handleRecordingError(err) {
			let errorMessage = this.$t('audioRecordingNotSupported');

			if (err.name === 'NotAllowedError') {
				errorMessage = this.$t('microphoneAccessDenied');
			} else if (err.name === 'NotFoundError') {
				errorMessage = this.$t('microphoneNotFound');
			} else if (err.name === 'NotSupportedError') {
				errorMessage = this.$t('audioRecordingNotSupported');
			}

			this.$q.notify({
				type: 'negative',
				message: errorMessage
			});
		},

		stopRecording() {
			if (this.mediaRecorder && this.isRecording) {
				this.mediaRecorder.stop();
			} else {
				this.clearRecording();
			}
		},

		cancelRecording() {
			this.isCancelling = true;
			this.stopRecording();
		},

		clearRecording() {
			this.clearTimer();
			if (this.stream) {
				this.stream.getTracks().forEach((t) => t.stop());
				this.stream = null;
			}
			this.isRecording = false;
			this.isLocked = false;
			this.isCancelling = false;
			this.showGestureHints = false;
			this.isDraggingUp = false;
			this.isDraggingRight = false;
		},

		clearTimer() {
			if (this.timerInterval) {
				clearInterval(this.timerInterval);
				this.timerInterval = null;
			}
		}
	},
	mounted() {
		this.initializeMicrophone();
	},
	beforeUnmount() {
		this.removeDragListeners();
		this.clearRecording();
	}
});
</script>

<style lang="scss">
.file-uploader {
	// Анимации
	.slide-fade-enter-active {
		transition: all 0.3s ease-out;
	}

	.slide-fade-leave-active {
		transition: all 0.2s ease-in;
	}

	.slide-fade-enter-from {
		opacity: 0;
		transform: translateX(-10px);
	}

	.slide-fade-leave-to {
		opacity: 0;
		transform: translateX(10px);
	}

	.fade-enter-active {
		transition: all 0.3s ease;
	}

	.fade-leave-active {
		transition: all 0.2s ease;
	}

	.fade-enter-from {
		opacity: 0;
		transform: translateY(10px);
	}

	.fade-leave-to {
		opacity: 0;
		transform: translateY(-10px);
	}
}
</style>

<style scoped lang="scss">
.recording-interface {
	width: 100%;
	position: relative;
	display: flex;
	justify-content: center;
}

.recording-container {
	display: flex;
	width: 100%;
	align-items: center;
	gap: 12px;
	position: relative;
	z-index: 10;
}

.microphone-btn {
	transition: all 0.3s ease;
	position: relative;
	z-index: 20;

	&.recording-active {
		background-color: #ff4444 !important;
		transform: scale(1.1);

		:deep(.q-icon) {
			animation: pulse 1.5s infinite;
		}
	}

	&.locked-mode {
		background-color: #4caf50 !important;
	}

	&.cancel-mode {
		background-color: #ff4444 !important;
		transform: scale(0.9);
	}
}

@keyframes pulse {
	0% {
		opacity: 1;
	}
	50% {
		opacity: 0.5;
	}
	100% {
		opacity: 1;
	}
}
</style>
